import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Banner } from "shared/components/Banner";
import {
  isLeadSelected,
  type LeadSelection
} from "shared/app/LeadsTableView/hooks/useLeadSelection";

const pluralLeads = (count: number) => (count === 1 ? "lead" : "leads");

/**
 * Gmail-style hand-off from "this page is selected" to "every lead matching the
 * filters is selected" (CRM-1449). Renders nothing unless one of the two states applies.
 */
export const LeadSelectionBanner = ({
  selection,
  count,
  totalCount,
  pageLeadIds,
  isFiltered,
  onSelectAllMatching,
  onClear
}: {
  selection: LeadSelection;
  count: number;
  totalCount: number;
  pageLeadIds: number[];
  /** Whether anything narrows the list (filters, search, assignee, starred, type). */
  isFiltered: boolean;
  onSelectAllMatching: () => void;
  onClear: () => void;
}) => {
  const scopeLabel = isFiltered ? "matching your filters" : "in your account";

  if (selection.mode === "filter") {
    const excluded = selection.excludedIds.size;
    return (
      <Banner
        variant="primary"
        HugeIcon={
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            size={20}
            className="text-primary-500"
          />
        }
        body={`All ${count.toLocaleString()} ${pluralLeads(count)} ${scopeLabel} are selected${
          excluded > 0
            ? `, ${excluded.toLocaleString()} un-ticked on this page`
            : ""
        }. Bulk actions will apply beyond the current page.`}
        primaryButton={{ text: "Clear selection", onClick: onClear }}
      />
    );
  }

  const wholePageSelected =
    pageLeadIds.length > 0 &&
    pageLeadIds.every((id) => isLeadSelected(selection, id));
  if (!wholePageSelected || totalCount <= pageLeadIds.length) return null;

  return (
    <Banner
      variant="primary"
      HugeIcon={
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={20}
          className="text-primary-500"
        />
      }
      body={`All ${pageLeadIds.length} ${pluralLeads(pageLeadIds.length)} on this page are selected.`}
      primaryButton={{
        text: `Select all ${totalCount.toLocaleString()} ${pluralLeads(totalCount)} ${scopeLabel}`,
        onClick: onSelectAllMatching
      }}
    />
  );
};
